import api, { api_v2 } from "@/api/axios.config"
import {
  CalendarioDataType,
  CalendarioMeta,
  EventoDataType,
  EventSubscriptionType,
  GetCalendarioParamsDataType,
  Inscricao,
} from "@/types/events"

// --- Tipos internos da resposta V2 (camelCase) ---

interface V2PessoaInscrita {
  pesId: number
  pesUid: string
  pesNome: string
  empFantasia: string
  fotoUrl: string
}

interface V2ItemCalendario {
  id: number
  turma: number
  descricao: string
  dataInicio: string
  dataLimite: string
  dataFim: string
  local: string
  endereco: string
  urlFichaTecnica?: string | null
  status: number
  origem: string
  inscritos: string
  temFoto: number
  evenLinkZoom?: string | null
  pendentes: string
  autorizados: string
  recusados: string
  formato: string
  formatoId: number
  pessoasInsc: V2PessoaInscrita[]
  inscrito: boolean
  eveFotoUrl?: string
  imeFotoUrl?: string
  linkInscricao?: string | null
}

interface V2CalendarioEnvelope {
  success: boolean
  message: string
  data: V2ItemCalendario[]
  meta: CalendarioMeta
}

// Converte item camelCase da V2 para o snake_case usado internamente
const mapV2ToEvento = (item: V2ItemCalendario): EventoDataType => ({
  id: item.id,
  turma: item.turma,
  descricao: item.descricao,
  data_inicio: item.dataInicio,
  data_limite: item.dataLimite,
  data_fim: item.dataFim,
  local: item.local,
  endereco: item.endereco,
  url_ficha_tecnica: item.urlFichaTecnica ?? undefined,
  status: item.status,
  origem: item.origem,
  inscritos: item.inscritos,
  tem_foto: item.temFoto,
  even_link_zoom: item.evenLinkZoom ?? null,
  pendentes: item.pendentes,
  autorizados: item.autorizados,
  recusados: item.recusados,
  formato: item.formato,
  formato_id: item.formatoId,
  pessoas_insc: item.pessoasInsc.map((p) => ({
    pes_id: p.pesId,
    pes_uid: p.pesUid,
    pes_nome: p.pesNome,
    emp_fantasia: p.empFantasia,
    foto_url: p.fotoUrl,
  })),
  inscrito: item.inscrito,
  eve_foto_url: item.eveFotoUrl,
  ime_foto_url: item.imeFotoUrl,
  link_inscricao: item.linkInscricao ?? null,
})

// --- Endpoints V2 ---

export const getCalendarioAction = async (params?: GetCalendarioParamsDataType) => {
  // pes_id e demais params de V1 são lidos do token em V2
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { pes_id, pes_email, qtd_pessoas, id_origem, ...v2Params } = params ?? {}
  const response = await api_v2.get<V2CalendarioEnvelope>("calendario", { params: v2Params })
  return {
    data: {
      data: response.data.data.map(mapV2ToEvento),
      total: response.data.meta.total,
      meta: response.data.meta,
    } as CalendarioDataType,
  }
}

// Mantém V1 pois o endpoint V2 /pessoa lê pes_id do token (sem suporte a ver outra pessoa)
export const getCalendarioForPessoaAction = async (params?: GetCalendarioParamsDataType) => {
  const queryString = new URLSearchParams(params as Record<string, string>).toString()
  return api.get<CalendarioDataType>(`/calendario/pessoa${queryString ? `?${queryString}` : ""}`)
}

export const subscribeEvent = async (payload: EventSubscriptionType) => {
  return api_v2.post(`plataforma/inscricao`, payload)
}

// TODO: Implement API calls
export const getInscricoesByEvento = async (eventoId: number): Promise<Inscricao[]> => {
  console.log(eventoId)
  // Mock data - replace with API call
  return Promise.resolve([
    {
      id: 1,
      nome: "João da Silva",
      email: "joao.silva@example.com",
      status: "Pendente",
    },
    {
      id: 2,
      nome: "Maria Oliveira",
      email: "maria.oliveira@example.com",
      status: "Aprovado",
    },
    {
      id: 3,
      nome: "Carlos Pereira",
      email: "carlos.pereira@example.com",
      status: "Reprovado",
    },
    {
      id: 4,
      nome: "Ana Souza",
      email: "ana.souza@example.com",
      status: "Pendente",
    },
  ])
}

interface SubscriptionData {
  pes_id: number[]
  originador_id: number
  origem_id?: number
  id_inscricao?: number
  origem: string
  status: string
  motivo?: string
}

export const approveInscricao = async (payload: SubscriptionData): Promise<void> => {
  return api_v2.post("plataforma/revisa-inscricao", payload)
}

export const reproveInscricao = async (payload: SubscriptionData): Promise<void> => {
  return api_v2.post("plataforma/revisa-inscricao", payload)
}

export interface BatchSubscriptionDataType {
  pessoas: string[]
  id_produto: number
  origem: string
  turma: number | null
}

export const batchSubscribeEvents = async (payload: BatchSubscriptionDataType): Promise<void> => {
  return api_v2.post("/plataforma/inscricoes/importacao", payload)
}

export const updateTechnicalFile = async (payload: FormData): Promise<void> => {
  return api_v2.post(`/plataforma/ficha-tecnica/arquivo`, payload, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })
}

export const updateTechnicalFilePatch = async (payload: FormData): Promise<void> => {
  return api_v2.put(`/plataforma/ficha-tecnica/arquivo`,  payload, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })
}

export const deleteTechnicalFile = async (id: number, origem: string): Promise<void> => {
  return api_v2.delete(`/plataforma/ficha-tecnica/arquivo/${id}?origem=${origem}`)
}

// TODO: substituir pelo endpoint real quando disponível
export const updateEventZoomLink = async (id: number, even_link_zoom: string): Promise<void> => {
  return api_v2.patch(`/plataforma/evento/${id}/zoom`, { link_zoom: even_link_zoom })
}
